import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { z } from "zod"
import { SignIn } from "@/auth/components/sign-in"
import {
  getLoginPageData,
  loginFormAction,
  loginFormSchema,
  requireAuthConfiguration,
  requireGuestUser,
} from "@/auth/rpc"
import { Turnstile } from "@/cloudflare/turnstile"
import { useFormAction } from "@/form"

function isSsoFailedFlag(v: unknown): boolean {
  return (
    v === true ||
    v === 1 ||
    v === "1" ||
    v === "true" ||
    v === '"true"' ||
    v === '"1"'
  )
}

const loginSearchSchema = z.object({
  redirectTo: z.string().optional(),
  // Query may be string/boolean depending on router serialization.
  ssoFailed: z.unknown().optional(),
})

export const Route = createFileRoute("/login")({
  validateSearch: (search) => loginSearchSchema.parse(search),
  beforeLoad: async () => {
    await requireAuthConfiguration()
    await requireGuestUser()
  },
  loaderDeps: ({ search }) => ({
    redirectTo: search.redirectTo ?? "/",
  }),
  loader: async ({ deps }) => {
    return getLoginPageData({ data: { redirectTo: deps.redirectTo } })
  },
  component: Login,
})

function Login() {
  const {
    cloudflareTurnstilePublicKey,
    redirectTo,
    allowRegistration,
    sisterAppUrl,
  } = Route.useLoaderData()
  const search = Route.useSearch()
  const queryClient = useQueryClient()
  const ssoFailed = isSsoFailedFlag(search.ssoFailed)
  const [ssoPending, setSsoPending] = useState(
    () => Boolean(sisterAppUrl) && !ssoFailed,
  )

  useEffect(() => {
    if (!sisterAppUrl || ssoFailed) {
      setSsoPending(false)
      return
    }
    const consume = new URL("/sso/consume", window.location.origin)
    consume.searchParams.set("redirectTo", redirectTo)
    const issue = new URL("/sso/issue", sisterAppUrl)
    issue.searchParams.set("returnTo", consume.toString())
    window.location.replace(issue.toString())
  }, [sisterAppUrl, ssoFailed, redirectTo])

  const { data, error, isPending, onSubmit } = useFormAction(
    loginFormSchema,
    loginFormAction,
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["user"] })
      },
    },
  )
  const { email, mode, error: actionError } = data ?? {}
  const turnstileSubjectKey = isPending ? "pending" : "idle"

  if (ssoPending) {
    return (
      <main className="container mx-auto flex h-screen flex-col items-center justify-center gap-2">
        <p className="text-muted-foreground text-sm">
          Checking for an existing session on the other app…
        </p>
      </main>
    )
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="container mx-auto mt-4 space-y-4"
    >
      <main className="flex h-screen flex-col items-center justify-center gap-4">
        <input type="hidden" name="redirectTo" value={redirectTo} />
        <SignIn
          isPending={isPending}
          email={email}
          mode={mode}
          error={actionError ?? error?.message}
          allowRegistration={allowRegistration}
        />
        {cloudflareTurnstilePublicKey && (
          <Turnstile
            siteKey={cloudflareTurnstilePublicKey}
            subjectKey={turnstileSubjectKey}
          />
        )}
        <p className="text-muted-foreground text-sm">
          Same account works on Devices &amp; SIMs and Office Supplies
        </p>
      </main>
    </form>
  )
}
